import { motion } from "framer-motion";

/**
 * Lightweight emotion-driven animated backgrounds. Renders behind main content.
 * Positioned absolutely fill; parent must be relative.
 */

const rand = (min, max) => Math.random() * (max - min) + min;

const seed = (n, fn) => Array.from({ length: n }).map((_, i) => fn(i));

// ------- Individual scenes -------

function WarmLove() {
  const hearts = seed(14, (i) => ({
    x: rand(0, 100), delay: i * 0.6, dur: rand(6, 12), size: rand(10, 18),
  }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 800px at 30% 30%, rgba(217, 119, 87, 0.14), transparent 65%)" }}>
      {hearts.map((h, i) => (
        <motion.div key={i}
          initial={{ y: "110%", x: `${h.x}%`, opacity: 0 }}
          animate={{ y: "-10%", opacity: [0, 0.6, 0] }}
          transition={{ duration: h.dur, delay: h.delay, repeat: Infinity, ease: "linear" }}
          className="absolute"
        >
          <svg width={h.size} height={h.size} viewBox="0 0 24 24">
            <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" fill="#D97757" opacity="0.75" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

function MissingYou() {
  const drops = seed(60, (i) => ({
    x: rand(0, 100), delay: rand(0, 3), dur: rand(1.2, 2.4), len: rand(14, 30),
  }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(180,190,205,0.14) 0%, rgba(120,130,150,0.05) 100%)" }}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(1000px 600px at 60% 20%, rgba(160,180,210,0.18), transparent 60%)" }} />
      {drops.map((d, i) => (
        <motion.div key={i}
          initial={{ y: "-10%", x: `${d.x}%`, opacity: 0 }}
          animate={{ y: "110%", opacity: [0, 0.55, 0] }}
          transition={{ duration: d.dur, delay: d.delay, repeat: Infinity, ease: "linear" }}
          className="absolute"
          style={{ width: 1, height: d.len, background: "linear-gradient(180deg, rgba(120,140,170,0.75), transparent)" }}
        />
      ))}
    </div>
  );
}

function Sorry() {
  const mist = seed(6, (i) => ({ x: rand(0, 100), y: rand(0, 100), size: rand(280, 500), delay: i * 1.4 }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(140,150,170,0.10) 0%, rgba(70,85,105,0.06) 100%)" }}>
      {mist.map((m, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, x: `${m.x}%`, y: `${m.y}%`, scale: 0.9 }}
          animate={{ opacity: [0, 0.35, 0], scale: [0.9, 1.15, 0.95] }}
          transition={{ duration: 14, delay: m.delay, repeat: Infinity }}
          className="absolute rounded-full"
          style={{ width: m.size, height: m.size, background: "radial-gradient(circle, rgba(200,215,230,0.35), transparent 70%)", filter: "blur(30px)" }}
        />
      ))}
    </div>
  );
}

function Birthday() {
  const sparks = seed(45, (i) => ({ x: rand(0, 100), y: rand(0, 100), delay: rand(0, 4), dur: rand(2, 4), size: rand(3, 7) }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 700px at 50% 30%, rgba(217, 119, 6, 0.14), transparent 60%)" }}>
      {sparks.map((s, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, x: `${s.x}%`, y: `${s.y}%`, scale: 0 }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1, 0.5] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity }}
          className="absolute rounded-full"
          style={{ width: s.size, height: s.size, background: "#F5B857", boxShadow: "0 0 8px #F5B857" }}
        />
      ))}
    </div>
  );
}

function Romantic() {
  const petals = seed(24, (i) => ({ x: rand(0, 100), delay: i * 0.5, dur: rand(10, 18), size: rand(10, 18), rot: rand(0, 360) }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 800px at 60% 20%, rgba(219, 39, 119, 0.10), transparent 65%)" }}>
      {petals.map((p, i) => (
        <motion.div key={i}
          initial={{ y: "-10%", x: `${p.x}%`, rotate: p.rot, opacity: 0 }}
          animate={{ y: "110%", x: [`${p.x}%`, `${p.x + rand(-6, 6)}%`], rotate: p.rot + 180, opacity: [0, 0.8, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, ease: "linear" }}
          className="absolute"
        >
          <svg width={p.size} height={p.size} viewBox="0 0 24 24">
            <path d="M12 3 C 8 8 8 14 12 21 C 16 14 16 8 12 3 Z" fill="#F5B4C8" opacity="0.85" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

function LongDistance() {
  const stars = seed(70, (i) => ({ x: rand(0, 100), y: rand(0, 100), size: rand(1, 3), delay: rand(0, 5), dur: rand(2, 5) }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 800px at 50% 30%, rgba(90, 110, 180, 0.16), transparent 60%)" }}>
      {stars.map((s, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, x: `${s.x}%`, y: `${s.y}%` }}
          animate={{ opacity: [0, 1, 0.2] }}
          transition={{ duration: s.dur, delay: s.delay, repeat: Infinity }}
          className="absolute rounded-full"
          style={{ width: s.size, height: s.size, background: "#EEF2FF", boxShadow: "0 0 6px rgba(238,242,255,0.6)" }}
        />
      ))}
    </div>
  );
}

function Gratitude() {
  const leaves = seed(18, (i) => ({ x: rand(0, 100), delay: i * 0.7, dur: rand(12, 20), size: rand(12, 22), rot: rand(0, 360) }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 800px at 40% 40%, rgba(15, 118, 110, 0.10), transparent 65%)" }}>
      {leaves.map((l, i) => (
        <motion.div key={i}
          initial={{ y: "-10%", x: `${l.x}%`, rotate: l.rot, opacity: 0 }}
          animate={{
            y: "110%",
            x: [`${l.x}%`, `${l.x + rand(-8, 8)}%`, `${l.x + rand(-8, 8)}%`],
            rotate: l.rot + 360,
            opacity: [0, 0.8, 0],
          }}
          transition={{ duration: l.dur, delay: l.delay, repeat: Infinity, ease: "linear" }}
          className="absolute"
        >
          <svg width={l.size} height={l.size} viewBox="0 0 24 24">
            <path d="M4 20 C 10 4 20 8 20 8 C 20 8 16 18 4 20 Z" fill="#A6B18A" opacity="0.85" />
            <path d="M6 18 L 18 8" stroke="#3D4A2C" strokeWidth="0.5" fill="none" opacity="0.5" />
          </svg>
        </motion.div>
      ))}
    </div>
  );
}

function Celebration() {
  const confetti = seed(50, (i) => ({
    x: rand(0, 100), delay: rand(0, 3), dur: rand(6, 12),
    size: rand(3, 7), rot: rand(0, 360),
    color: ["#F5B857", "#D97757", "#9333EA", "#EBE0C8", "#991B1B"][i % 5],
  }));
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(1200px 800px at 50% 20%, rgba(147, 51, 234, 0.10), transparent 65%)" }}>
      {confetti.map((c, i) => (
        <motion.div key={i}
          initial={{ y: "-10%", x: `${c.x}%`, rotate: c.rot, opacity: 0 }}
          animate={{ y: "110%", rotate: c.rot + 720, opacity: [0, 1, 0] }}
          transition={{ duration: c.dur, delay: c.delay, repeat: Infinity, ease: "linear" }}
          className="absolute"
          style={{ width: c.size, height: c.size * 1.6, background: c.color, borderRadius: 1 }}
        />
      ))}
    </div>
  );
}

const THEMES = {
  warm_love: { name: "Warm love", icon: "❤️", scene: WarmLove, surface: "#FDFBF7", ink: "#7F1D1D" },
  missing_you: { name: "Missing you", icon: "🌧", scene: MissingYou, surface: "#F1F3F6", ink: "#3730A3" },
  sorry: { name: "I'm sorry", icon: "🌊", scene: Sorry, surface: "#F0F2F5", ink: "#1E3A5F" },
  birthday: { name: "Happy birthday", icon: "🔥", scene: Birthday, surface: "#FDFBF7", ink: "#7C2D12" },
  romantic: { name: "Romantic", icon: "🌸", scene: Romantic, surface: "#FCF5F7", ink: "#831843" },
  long_distance: { name: "Long distance", icon: "✨", scene: LongDistance, surface: "#F0EEF7", ink: "#134E4A" },
  gratitude: { name: "Gratitude", icon: "🌿", scene: Gratitude, surface: "#F4F6EF", ink: "#3F2A00" },
  celebration: { name: "Celebration", icon: "🎉", scene: Celebration, surface: "#F9F2FA", ink: "#6B21A8" },
};

export const themeById = (id) => THEMES[id] || THEMES.warm_love;

export default function EmotionTheme({ emotionId, children }) {
  const t = themeById(emotionId);
  const Scene = t.scene;
  return (
    <div className="relative min-h-screen w-full overflow-hidden" style={{ backgroundColor: t.surface }}>
      <Scene />
      <div className="relative z-10">{children}</div>
    </div>
  );
}
